import type { PublicConfig } from '@medicail/shared';

/*
 * Amplify is loaded on demand so dev mode (and the first paint of the sign in screen) does not pay for it.
 * Everything here awaits `ready` before touching Amplify, so configuration always happens first.
 */
const loadAuth = () => import('aws-amplify/auth');

let ready: Promise<void> = Promise.resolve();
let configured = false;

export function configureCognito(cfg: NonNullable<PublicConfig['cognito']>): void {
  if (configured) return;
  configured = true;
  ready = import('aws-amplify').then(({ Amplify }) => {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: cfg.userPoolId,
          userPoolClientId: cfg.userPoolClientId,
          loginWith: { email: true },
          signUpVerificationMethod: 'code',
        },
      },
    });
  });
}

async function auth() {
  await ready;
  return loadAuth();
}

/** ID token for the API. fetchAuthSession refreshes expired tokens automatically. */
export async function cognitoToken(): Promise<string | null> {
  try {
    const { fetchAuthSession } = await auth();
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export type SignInOutcome = 'done' | 'confirm_sign_up' | 'reset_password' | 'unsupported';

export async function cognitoSignIn(email: string, password: string): Promise<SignInOutcome> {
  try {
    const { signIn } = await auth();
    const { nextStep } = await signIn({ username: email, password });
    switch (nextStep.signInStep) {
      case 'DONE':
        return 'done';
      case 'CONFIRM_SIGN_UP':
        return 'confirm_sign_up';
      case 'RESET_PASSWORD':
        return 'reset_password';
      default:
        return 'unsupported';
    }
  } catch (err) {
    if ((err as Error).name === 'UserAlreadyAuthenticatedException') return 'done';
    throw err;
  }
}

export async function cognitoSignUp(name: string, email: string, password: string): Promise<'confirm' | 'done'> {
  const { signUp } = await auth();
  const { nextStep } = await signUp({
    username: email,
    password,
    options: { userAttributes: { email, name } },
  });
  return nextStep.signUpStep === 'DONE' ? 'done' : 'confirm';
}

export async function cognitoConfirmSignUp(email: string, code: string): Promise<void> {
  const { confirmSignUp } = await auth();
  await confirmSignUp({ username: email, confirmationCode: code.trim() });
}

export async function cognitoResendCode(email: string): Promise<void> {
  const { resendSignUpCode } = await auth();
  await resendSignUpCode({ username: email });
}

export async function cognitoRequestReset(email: string): Promise<void> {
  const { resetPassword } = await auth();
  await resetPassword({ username: email });
}

export async function cognitoConfirmReset(email: string, code: string, newPassword: string): Promise<void> {
  const { confirmResetPassword } = await auth();
  await confirmResetPassword({ username: email, confirmationCode: code.trim(), newPassword });
}

export async function cognitoSignOut(): Promise<void> {
  try {
    const { signOut } = await auth();
    await signOut();
  } catch {
    /* already signed out */
  }
}

/** Translate Cognito exceptions into friendly copy. */
export function friendlyAuthError(err: unknown): string {
  const e = err as { name?: string; message?: string };
  switch (e?.name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      if (/disabled/i.test(e.message ?? '')) return 'This account has been disabled. Please contact support.';
      return 'Incorrect email or password.';
    case 'UsernameExistsException':
      return 'An account with this email already exists. Try signing in instead.';
    case 'CodeMismatchException':
      return 'That code is not right. Check it and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one.';
    case 'InvalidPasswordException':
      return 'That password does not meet the requirements.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'InvalidParameterException':
      return e.message || 'Please check the details and try again.';
    case 'NetworkError':
      return 'Network problem. Check your connection and try again.';
    default:
      return e?.message || 'Something went wrong. Please try again.';
  }
}
